import { useState, useRef, useEffect } from 'react';
import {
  DndContext, closestCenter, DragEndEvent,
  useSensor, useSensors, PointerSensor,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy,
  useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft, Save, Eye, Loader2, GripVertical, Trash2, Plus,
  Type, AlignLeft, Image, MousePointer, Minus, MoveVertical, Share2, FileText,
  Smartphone, Monitor, Sparkles, ChevronUp, ChevronDown,
} from 'lucide-react';
import { BLOCK_DEFS, createBlock, renderBlocksToHtml, type EmailBlock, type BlockType } from '@/lib/emailBlocks';
import { SelectField } from '@/components/ui/SelectField';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';

// ─── Image field: URL + upload + "use my logo" ─────────────────────────────
// Uploads go to /email-assets and come back as a public URL that every mail
// client can load (inline/base64 images get blocked by Gmail/Outlook).
function ImageField({ label, value, onChange, logoButtons = true }: {
  label: string; value: string; onChange: (v: string) => void; logoButtons?: boolean;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const inputCls = 'w-full h-8 px-2 rounded border border-slate-200 text-xs focus:outline-none focus:ring-1 focus:ring-[#6366F1]';
  const chip = 'text-[10px] px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-100';

  const upload = async (file: File) => {
    if (file.size > 2 * 1024 * 1024) { toast('error', 'Image must be 2 MB or smaller'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/email-assets', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(data.url);
      toast('success', 'Image uploaded');
    } catch (e: any) {
      toast('error', e?.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div>
      <label className="text-[11px] font-medium text-slate-500 mb-1 block">{label}</label>
      <input className={inputCls} value={value} placeholder="https://… or upload" onChange={e => onChange(e.target.value)} />
      <div className="flex flex-wrap gap-1 mt-1.5">
        <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()} className={`${chip} inline-flex items-center gap-1`}>
          {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Upload image
        </button>
        {logoButtons && (
          <>
            <button type="button" onClick={() => onChange('{{brand.logoUrl}}')} className={`${chip} font-mono`} title="Auto-fill each tenant's logo at send time">Use my logo</button>
            <button type="button" onClick={() => onChange('{{brand.logoRectangular}}')} className={`${chip} font-mono`}>Rectangular logo</button>
          </>
        )}
        {value && <button type="button" onClick={() => onChange('')} className="text-[10px] px-2 py-0.5 rounded text-slate-500 hover:bg-slate-100">Remove</button>}
      </div>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); }} />
      <p className="text-[10px] text-slate-400 mt-1">PNG, JPG, GIF or WebP · up to 2 MB</p>
    </div>
  );
}

// Brand tokens can't resolve in the editor — show a labelled stand-in.
function PreviewImg({ src, style }: { src: string; style: React.CSSProperties }) {
  if (/^\{\{\s*brand\./.test(src || '')) {
    return (
      <span style={{ display: 'inline-block', padding: '6px 12px', border: '1px dashed #818CF8', borderRadius: 6, background: '#EEF2FF', color: '#4F46E5', fontSize: 11, fontWeight: 600 }}>
        🏢 Your logo
      </span>
    );
  }
  return <img src={src} alt="" style={style} />;
}

const ICONS: Record<string, React.ElementType> = {
  Type, AlignLeft, Image, MousePointer, Minus, MoveVertical, Share2, FileText,
};

// ─── Block Properties Panel ─────────────────────────────────────────────────

function BlockProperties({ block, onChange, onDelete }: { block: EmailBlock; onChange: (props: Record<string, any>) => void; onDelete: () => void }) {
  const p = block.props;
  const set = (key: string, val: any) => onChange({ ...p, [key]: val });

  const inputCls = 'w-full h-8 px-2 rounded border border-slate-200 text-xs focus:outline-none focus:ring-1 focus:ring-[#6366F1]';
  const labelCls = 'text-[11px] font-medium text-slate-500 mb-1 block';

  return (
    <div className="space-y-3">
      <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">{block.type} Properties</p>

      {/* Text content */}
      {('text' in p) && (
        <div>
          <label className={labelCls}>Content</label>
          <textarea rows={4} className={`${inputCls} h-auto py-1.5 resize-none`}
            value={p.text} onChange={e => set('text', e.target.value)} />
          <p className="text-[10px] text-slate-400 mt-0.5">Use {'{{lead.name}}'} for personalization</p>
        </div>
      )}

      {/* URL */}
      {('url' in p) && block.type === 'image' && (
        <ImageField label="Image" value={p.url ?? ''} onChange={v => set('url', v)} />
      )}
      {('url' in p) && block.type !== 'image' && (
        <div>
          <label className={labelCls}>Link URL</label>
          <input className={inputCls} value={p.url} onChange={e => set('url', e.target.value)} />
        </div>
      )}

      {/* Header: logo + banner background · Footer: logo */}
      {block.type === 'header' && (
        <>
          <ImageField label="Logo (above the title)" value={p.imageUrl ?? ''} onChange={v => set('imageUrl', v)} />
          <ImageField label="Banner background image" value={p.bgImageUrl ?? ''} onChange={v => set('bgImageUrl', v)} logoButtons={false} />
        </>
      )}
      {block.type === 'footer' && (
        <ImageField label="Logo (above the footer text)" value={p.imageUrl ?? ''} onChange={v => set('imageUrl', v)} />
      )}

      {/* Font Size */}
      {('fontSize' in p) && (
        <div>
          <label className={labelCls}>Font Size ({p.fontSize}px)</label>
          <input type="range" min={10} max={48} value={p.fontSize} onChange={e => set('fontSize', Number(e.target.value))}
            className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#6366F1]" />
        </div>
      )}

      {/* Colors */}
      {('color' in p) && (
        <div className="flex items-center gap-2">
          <label className="text-[11px] font-normal text-slate-500">Text Color</label>
          <input type="color" value={p.color} onChange={e => set('color', e.target.value)} className="h-6 w-8 rounded border cursor-pointer" />
        </div>
      )}
      {('bgColor' in p) && (
        <div className="flex items-center gap-2">
          <label className="text-[11px] font-normal text-slate-500">Background</label>
          <input type="color" value={p.bgColor} onChange={e => set('bgColor', e.target.value)} className="h-6 w-8 rounded border cursor-pointer" />
        </div>
      )}
      {('textColor' in p && block.type !== 'text') && (
        <div className="flex items-center gap-2">
          <label className="text-[11px] font-normal text-slate-500">Text Color</label>
          <input type="color" value={p.textColor} onChange={e => set('textColor', e.target.value)} className="h-6 w-8 rounded border cursor-pointer" />
        </div>
      )}

      {/* Alignment */}
      {('alignment' in p) && (
        <div>
          <label className={labelCls}>Alignment</label>
          <div className="flex rounded border border-slate-200 overflow-hidden">
            {['left', 'center', 'right'].map(a => (
              <button key={a} onClick={() => set('alignment', a)}
                className={`flex-1 py-1.5 text-[11px] font-medium capitalize ${p.alignment === a ? 'bg-[#6366F1] text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
                {a}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bold / Italic */}
      {('bold' in p) && (
        <div className="flex gap-2">
          <button onClick={() => set('bold', !p.bold)}
            className={`px-3 py-1.5 text-xs rounded border ${p.bold ? 'bg-[#6366F1] text-white border-[#6366F1]' : 'border-slate-200 text-slate-600'}`}>
            <strong>B</strong>
          </button>
          <button onClick={() => set('italic', !p.italic)}
            className={`px-3 py-1.5 text-xs rounded border ${p.italic ? 'bg-[#6366F1] text-white border-[#6366F1]' : 'border-slate-200 text-slate-600'}`}>
            <em>I</em>
          </button>
        </div>
      )}

      {/* Image width */}
      {block.type === 'image' && (
        <div>
          <label className={labelCls}>Width</label>
          <SelectField className={inputCls} value={p.width} onChange={e => set('width', e.target.value)}>
            <option value="100%">Full Width</option>
            <option value="75%">75%</option>
            <option value="50%">50%</option>
          </SelectField>
        </div>
      )}

      {/* Button border radius */}
      {block.type === 'button' && (
        <div>
          <label className={labelCls}>Roundness ({p.borderRadius}px)</label>
          <input type="range" min={0} max={24} value={p.borderRadius} onChange={e => set('borderRadius', Number(e.target.value))}
            className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#6366F1]" />
        </div>
      )}

      {/* Spacer height */}
      {block.type === 'spacer' && (
        <div>
          <label className={labelCls}>Height ({p.height}px)</label>
          <input type="range" min={8} max={100} value={p.height} onChange={e => set('height', Number(e.target.value))}
            className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#6366F1]" />
        </div>
      )}

      {/* Divider */}
      {block.type === 'divider' && (
        <div>
          <label className={labelCls}>Thickness ({p.thickness}px)</label>
          <input type="range" min={1} max={5} value={p.thickness} onChange={e => set('thickness', Number(e.target.value))}
            className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#6366F1]" />
        </div>
      )}

      {/* Social links */}
      {block.type === 'social' && (
        <div className="space-y-2">
          {['facebook', 'twitter', 'linkedin', 'instagram'].map(s => (
            <div key={s}>
              <label className={labelCls}>{s.charAt(0).toUpperCase() + s.slice(1)}</label>
              <input className={inputCls} placeholder={`https://${s}.com/...`} value={p[s] || ''} onChange={e => set(s, e.target.value)} />
            </div>
          ))}
        </div>
      )}

      {/* Padding */}
      {('padding' in p) && (
        <div>
          <label className={labelCls}>Padding ({p.padding}px)</label>
          <input type="range" min={0} max={48} value={p.padding} onChange={e => set('padding', Number(e.target.value))}
            className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#6366F1]" />
        </div>
      )}

      {/* Delete Block */}
      <div className="pt-4 mt-4 border-t border-slate-100">
        <button onClick={onDelete}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-red-200 text-red-500 text-xs font-semibold hover:bg-red-50 transition-colors">
          <Trash2 className="h-3.5 w-3.5" /> Delete This Block
        </button>
      </div>
    </div>
  );
}

// ─── Sortable Block in Canvas ───────────────────────────────────────────────

function SortableBlock({
  block,
  isSelected,
  canMoveUp,
  canMoveDown,
  onClick,
  onDelete,
  onMoveUp,
  onMoveDown,
}: {
  block: EmailBlock;
  isSelected: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onClick: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  // Controls are always visible when the block is selected so users on
  // large canvases / touch devices aren't hunting for them. Otherwise
  // they fade in on hover to keep the canvas clean.
  const controlsVisibility = isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100';

  const btnCls =
    'h-6 w-6 flex items-center justify-center rounded bg-white shadow border border-slate-200 text-slate-500 hover:text-[#6366F1] hover:border-[#6366F1] transition-colors disabled:opacity-30 disabled:cursor-not-allowed';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group cursor-pointer ${isSelected ? 'ring-2 ring-[#6366F1] rounded-lg' : ''}`}
      onClick={onClick}
    >
      {/* Floating toolbar — sits on the right edge inside the block so it
          stays visible even when the canvas has no left margin. */}
      <div
        className={`absolute right-2 top-2 z-10 flex items-center gap-1 ${controlsVisibility} transition-opacity`}
      >
        <button
          type="button"
          {...attributes}
          {...listeners}
          title="Drag to reorder"
          className={`${btnCls} cursor-grab active:cursor-grabbing`}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          title="Move up"
          disabled={!canMoveUp}
          onClick={(e) => {
            e.stopPropagation();
            onMoveUp();
          }}
          className={btnCls}
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          title="Move down"
          disabled={!canMoveDown}
          onClick={(e) => {
            e.stopPropagation();
            onMoveDown();
          }}
          className={btnCls}
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          title="Delete block"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className={`${btnCls} hover:text-red-600 hover:border-red-300`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <CanvasBlock block={block} />
    </div>
  );
}

// ─── Canvas Block Preview ───────────────────────────────────────────────────

function CanvasBlock({ block }: { block: EmailBlock }) {
  const p = block.props;

  switch (block.type) {
    case 'header':
      return (
        <div style={{
          backgroundColor: p.bgColor, padding: `${p.padding}px 24px`, textAlign: p.alignment,
          ...(p.bgImageUrl && !/^\{\{/.test(p.bgImageUrl) ? { backgroundImage: `url('${p.bgImageUrl}')`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}),
        }}>
          {p.imageUrl && <div style={{ marginBottom: p.text ? 12 : 0 }}><PreviewImg src={p.imageUrl} style={{ maxHeight: 56, maxWidth: '100%' }} /></div>}
          {p.text && <h2 style={{ color: p.textColor || '#fff', fontSize: p.fontSize, fontWeight: 700, margin: 0 }}>{p.text}</h2>}
        </div>
      );
    case 'text':
      return (
        <div style={{ padding: `${p.padding}px 24px`, textAlign: p.alignment }}>
          <p style={{ color: p.color, fontSize: p.fontSize, fontWeight: p.bold ? 700 : 400, fontStyle: p.italic ? 'italic' : 'normal', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {p.text}
          </p>
        </div>
      );
    case 'image': {
      // In the editor canvas, brand.* tokens won't resolve — show a
      // labeled placeholder so the user sees "this is your logo" instead
      // of a broken image icon.
      const isBrandVar = typeof p.url === 'string' && /^\{\{\s*brand\./.test(p.url);
      return (
        <div style={{ padding: `${p.padding}px 24px`, textAlign: p.alignment }}>
          {isBrandVar ? (
            <div
              style={{
                display: 'inline-block',
                width: p.width,
                maxWidth: '100%',
                padding: '28px 20px',
                background: 'linear-gradient(135deg, #EEF2FF, #E0E7FF)',
                border: '1px dashed #818CF8',
                borderRadius: 8,
                color: '#4F46E5',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              🏢 Your logo here (auto-filled at send time)
              <div style={{ fontSize: 10, fontWeight: 400, opacity: 0.7, marginTop: 4, fontFamily: 'monospace' }}>
                {p.url}
              </div>
            </div>
          ) : (
            <img src={p.url} alt={p.alt} style={{ width: p.width, maxWidth: '100%', borderRadius: 4 }} />
          )}
        </div>
      );
    }
    case 'button':
      return (
        <div style={{ padding: `${p.padding}px 24px`, textAlign: p.alignment }}>
          <span style={{ display: 'inline-block', background: p.bgColor, color: p.textColor, padding: '10px 24px', borderRadius: p.borderRadius, fontSize: 14, fontWeight: 600 }}>
            {p.text}
          </span>
        </div>
      );
    case 'divider':
      return (
        <div style={{ padding: `${p.padding}px 24px` }}>
          <hr style={{ border: 'none', borderTop: `${p.thickness}px solid ${p.color}`, width: `${p.width}%`, margin: '0 auto' }} />
        </div>
      );
    case 'spacer':
      return <div style={{ height: p.height, background: 'repeating-linear-gradient(45deg, transparent, transparent 4px, #f1f5f9 4px, #f1f5f9 8px)' }} />;
    case 'social': {
      const links = ['facebook', 'twitter', 'linkedin', 'instagram'].filter(s => p[s]);
      return (
        <div style={{ padding: `${p.padding}px 24px`, textAlign: p.alignment }}>
          {links.length > 0 ? links.map(s => (
            <span key={s} style={{ margin: '0 6px', color: '#64748B', fontSize: 13 }}>{s.charAt(0).toUpperCase() + s.slice(1)}</span>
          )) : <span style={{ color: '#CBD5E1', fontSize: 13 }}>Add social links in properties</span>}
        </div>
      );
    }
    case 'footer':
      return (
        <div style={{ background: '#F8FAFC', padding: `${p.padding}px 24px`, textAlign: p.alignment, borderTop: '1px solid #E2E8F0' }}>
          {p.imageUrl && <div style={{ marginBottom: 8 }}><PreviewImg src={p.imageUrl} style={{ maxHeight: 32, maxWidth: '100%' }} /></div>}
          <p style={{ color: p.color, fontSize: p.fontSize, margin: 0, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{p.text}</p>
        </div>
      );
    default:
      return null;
  }
}

// ─── Subject Helper ─────────────────────────────────────────────────────
//
// Popover that sits next to the subject input. Two jobs:
//   1. One-click suggested subjects grouped by lifecycle event — pick one
//      and it replaces the current value. Good starting points that avoid
//      common mistakes (e.g. "Welcome to {{lead.name}}!" reading wrong).
//   2. Variable chips — click to insert at cursor position in the subject.

const SUBJECT_SUGGESTIONS: { group: string; subjects: string[] }[] = [
  {
    group: 'Lead Created (to lead)',
    subjects: [
      'Welcome to {{orgName}}, {{lead.firstName}}! 🎉',
      '{{lead.firstName}}, thanks for reaching out to {{orgName}}',
      'Hi {{lead.firstName}} — here\u2019s what happens next',
      'Your enquiry with {{orgName}} is received \u2713',
    ],
  },
  {
    group: 'Lead Assigned (to counselor)',
    subjects: [
      'New lead: {{lead.name}} ({{lead.phone}})',
      '\ud83d\udd14 Action needed \u2014 {{lead.name}} just came in',
      'Follow up required: {{lead.name}}',
    ],
  },
  {
    group: 'Status Changed (to lead)',
    subjects: [
      '{{lead.firstName}}, update on your {{orgName}} application',
      'Next steps for you, {{lead.firstName}}',
    ],
  },
  {
    group: 'Follow-up Reminders',
    subjects: [
      'Reminder: Follow-up with {{lead.name}} in 1 hour',
      'Your follow-ups for today',
    ],
  },
];

const SUBJECT_VARIABLES = [
  { label: 'First name', value: '{{lead.firstName}}' },
  { label: 'Full name', value: '{{lead.name}}' },
  { label: 'Email', value: '{{lead.email}}' },
  { label: 'Phone', value: '{{lead.phone}}' },
  { label: 'Status', value: '{{lead.status}}' },
  { label: 'Counselor', value: '{{counselor.name}}' },
  { label: 'Org name', value: '{{orgName}}' },
  { label: 'Org website', value: '{{brand.website}}' },
];

interface SubjectHelperProps {
  value: string;
  onChange: (next: string) => void;
}

function SubjectHelper({ value, onChange }: SubjectHelperProps) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!popoverRef.current) return;
      if (!popoverRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const insertAtCursor = (token: string) => {
    const el = inputRef.current;
    if (!el) {
      onChange(value + token);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    onChange(next);
    // Restore cursor after the inserted token
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Email Subject *"
          className="h-8 px-2 rounded border border-slate-200 text-sm w-64 focus:outline-none focus:ring-1 focus:ring-[#6366F1]"
        />
        <button
          type="button"
          onClick={() => setOpen((p) => !p)}
          title="Subject suggestions & variables"
          className={`h-8 w-8 flex items-center justify-center rounded border border-slate-200 ${
            open ? 'bg-indigo-50 border-indigo-300 text-indigo-600' : 'text-slate-500 hover:text-[#6366F1] hover:bg-slate-50'
          }`}
        >
          <Sparkles className="h-4 w-4" />
        </button>
      </div>

      {open && (
        <div
          ref={popoverRef}
          className="absolute top-10 left-0 z-40 w-[420px] max-h-[70vh] overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl"
        >
          {/* Variables */}
          <div className="px-4 py-3 border-b border-slate-100">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
              Insert variable
            </p>
            <div className="flex flex-wrap gap-1.5">
              {SUBJECT_VARIABLES.map((v) => (
                <button
                  key={v.value}
                  type="button"
                  onClick={() => insertAtCursor(v.value)}
                  className="text-[11px] font-mono px-2 py-1 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-100"
                >
                  {v.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5">
              Cursor position pe insert hoga. E.g. First name → <code className="text-indigo-500">{'{{lead.firstName}}'}</code>
            </p>
          </div>

          {/* Suggested subjects */}
          <div className="px-4 py-3">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
              Suggested subjects
            </p>
            <div className="space-y-3">
              {SUBJECT_SUGGESTIONS.map((grp) => (
                <div key={grp.group}>
                  <p className="text-[11px] font-semibold text-slate-700 mb-1">{grp.group}</p>
                  <div className="space-y-1">
                    {grp.subjects.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => {
                          onChange(s);
                          setOpen(false);
                        }}
                        className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-slate-50 text-slate-700 border border-transparent hover:border-slate-200"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-[10px] text-slate-500">
            Tip: keep subjects under 60 characters \u2014 mobile inbox truncates longer ones.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Email Builder ─────────────────────────────────────────────────────

interface Props {
  initialBlocks?: EmailBlock[];
  initialName?: string;
  initialSubject?: string;
  onSave: (data: { name: string; subject: string; blocks: EmailBlock[] }) => Promise<void>;
  onBack: () => void;
}

export function EmailBuilder({ initialBlocks, initialName, initialSubject, onSave, onBack }: Props) {
  const [name, setName] = useState(initialName || '');
  const [subject, setSubject] = useState(initialSubject || '');
  const [blocks, setBlocks] = useState<EmailBlock[]>(initialBlocks || []);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState<'edit' | 'desktop' | 'mobile'>('edit');

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const selectedBlock = blocks.find(b => b.id === selectedId) || null;

  const addBlock = (type: BlockType) => {
    const block = createBlock(type);
    setBlocks(prev => [...prev, block]);
    setSelectedId(block.id);
  };

  const updateBlockProps = (props: Record<string, any>) => {
    if (!selectedId) return;
    setBlocks(prev => prev.map(b => b.id === selectedId ? { ...b, props } : b));
  };

  const deleteBlock = (id: string) => {
    setBlocks(prev => prev.filter(b => b.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const moveBlock = (id: string, direction: -1 | 1) => {
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === id);
      if (idx === -1) return prev;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      return arrayMove(prev, idx, newIdx);
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setBlocks(prev => {
      const oldIdx = prev.findIndex(b => b.id === active.id);
      const newIdx = prev.findIndex(b => b.id === over.id);
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  const handleSave = async () => {
    if (!name.trim() || !subject.trim()) return;
    setSaving(true);
    await onSave({ name, subject, blocks });
    setSaving(false);
  };

  // ── Preview HTML ────────────────────────────────────────────────────────────
  // Shared with SettingsPage AutomationsTab preview via @/lib/emailBlocks —
  // keep the authored canvas and the downstream preview in lockstep.
  const fullPreviewHtml = `<html><body style="margin:0;padding:20px;background:#F1F5F9;font-family:Arial,sans-serif">${renderBlocksToHtml(blocks)}</body></html>`;

  return (
    <div className="fixed inset-0 z-50 bg-gray-100 flex flex-col">
      {/* Top Bar */}
      <div className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#6366F1]">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Template Name *"
            className="h-8 px-2 rounded border border-slate-200 text-sm font-medium w-48 focus:outline-none focus:ring-1 focus:ring-[#6366F1]" />
          <SubjectHelper value={subject} onChange={setSubject} />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded border border-slate-200 overflow-hidden">
            <button onClick={() => setPreviewMode('edit')}
              className={`px-2.5 py-1.5 text-xs ${previewMode === 'edit' ? 'bg-[#6366F1] text-white' : 'text-slate-500'}`}>Edit</button>
            <button onClick={() => setPreviewMode('desktop')}
              className={`px-2.5 py-1.5 ${previewMode === 'desktop' ? 'bg-[#6366F1] text-white' : 'text-slate-500'}`}>
              <Monitor className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => setPreviewMode('mobile')}
              className={`px-2.5 py-1.5 ${previewMode === 'mobile' ? 'bg-[#6366F1] text-white' : 'text-slate-500'}`}>
              <Smartphone className="h-3.5 w-3.5" />
            </button>
          </div>
          <button onClick={handleSave} disabled={saving || !name.trim() || !subject.trim()}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold rounded-lg disabled:opacity-60">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save
          </button>
        </div>
      </div>

      {/* Preview Mode */}
      {previewMode !== 'edit' ? (
        <div className="flex-1 overflow-auto flex justify-center py-8 bg-slate-100">
          <div style={{ width: previewMode === 'mobile' ? 375 : 650 }} className="bg-white rounded-xl shadow-xl overflow-hidden">
            <div dangerouslySetInnerHTML={{ __html: fullPreviewHtml }} />
          </div>
        </div>
      ) : (
        /* Edit Mode — 3 panels */
        <div className="flex-1 flex overflow-hidden">
          {/* Left: Block Palette */}
          <div className="w-48 bg-white border-r border-gray-200 p-3 flex-shrink-0 overflow-y-auto">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2">Add Blocks</p>
            <div className="space-y-1.5">
              {BLOCK_DEFS.map(def => {
                const Icon = ICONS[def.icon] || Type;
                return (
                  <button key={def.type} onClick={() => addBlock(def.type)}
                    className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm text-slate-600 hover:bg-indigo-50 hover:text-[#6366F1] transition-colors border border-transparent hover:border-indigo-200">
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="text-xs font-medium">{def.label}</span>
                    <Plus className="h-3 w-3 ml-auto opacity-0 group-hover:opacity-100" />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Center: Email Canvas */}
          <div className="flex-1 overflow-y-auto bg-slate-100 py-6 px-8">
            <div className="max-w-[600px] mx-auto bg-white rounded-xl shadow-lg overflow-hidden min-h-[400px]">
              {blocks.length === 0 ? (
                <div className="py-20 text-center">
                  <Type className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                  <p className="text-sm text-slate-400 font-medium">Click blocks on the left to start building</p>
                  <p className="text-xs text-slate-300 mt-1">Click to edit. Use ↑↓ buttons or drag to reorder.</p>
                </div>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
                    {blocks.map((block, idx) => (
                      <SortableBlock
                        key={block.id}
                        block={block}
                        isSelected={block.id === selectedId}
                        canMoveUp={idx > 0}
                        canMoveDown={idx < blocks.length - 1}
                        onClick={() => setSelectedId(block.id)}
                        onDelete={() => deleteBlock(block.id)}
                        onMoveUp={() => moveBlock(block.id, -1)}
                        onMoveDown={() => moveBlock(block.id, 1)}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </div>

          {/* Right: Properties Panel */}
          <div className="w-64 bg-white border-l border-gray-200 p-4 flex-shrink-0 overflow-y-auto">
            {selectedBlock ? (
              <BlockProperties block={selectedBlock} onChange={updateBlockProps} onDelete={() => deleteBlock(selectedBlock.id)} />
            ) : (
              <div className="text-center py-10">
                <Eye className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                <p className="text-xs text-slate-400">Click a block to edit its properties</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
